import React, { ReactElement, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { CheckIcon } from '../../assets/icons/CheckIcon';
import { ArrowIcon } from '../../assets/icons/ArrowIcon';
import { Typography } from '../Typography/Typography';
import { getDefaultLogo } from './utilities';
import { useClickOutside } from '../../hooks/useClickOutside';
import styles from './DropdownMenu.module.scss';
import classes from 'classnames/bind';

const cx = classes.bind(styles);

export type TOption = {
  id: string;
  name: string;
  logoUrl?: string;
};

export interface IDropdownMenuProps {
  options: TOption[];
  selectedOption: TOption;
  onOptionClick: (id: string) => void;
}

const DropdownMenu = ({ options, selectedOption, onOptionClick }: IDropdownMenuProps): ReactElement => {
  const [openMenu, setOpenMenu] = useState<boolean>(false);
  const nodeRef = useRef(null);

  const handleDropdownMenuClick = () => setOpenMenu((prevState) => !prevState);

  const handleOptionClick = (id: string) => {
    if (id !== selectedOption?.id) {
      onOptionClick(id);
    }
    setOpenMenu(false);
  };

  const domNode = useClickOutside<HTMLDivElement>(() => setOpenMenu(false));

  return (
    <>
      <div
        className={styles['selected-brand-container']}
        onClick={handleDropdownMenuClick}
        role="button"
        aria-label="brand-dropdown-menu"
        ref={domNode}
      >
        <img
          className={styles['brand-logo']}
          src={selectedOption?.logoUrl ?? getDefaultLogo(selectedOption?.name)}
          alt="Brand Logo"
        />
        <div className={styles['dropdown-label']} role="button" aria-label="brand-dropdown-menu-label">
          <Typography weight="semi-bold" size="S" color="primary" className={styles['dropdown-label']}>
            {selectedOption?.name}
          </Typography>
          <ArrowIcon className={styles['arrow']} />
        </div>
      </div>
      <CSSTransition nodeRef={nodeRef} in={openMenu} timeout={300} unmountOnExit>
        <div className={cx({ 'dropdown-closed': !openMenu }, 'dropdown-container')} ref={nodeRef}>
          <ul role="menu" className={styles['dropdown-list']}>
            {options.map((option: TOption) => (
              <li
                key={option.id}
                onClick={() => handleOptionClick(option?.id)}
                role="menuitem"
                className={styles['dropdown-item']}
              >
                <img className={styles['brand-logo']} src={option?.logoUrl ?? getDefaultLogo(option?.name)} />
                <div className={styles['dropdown-label']}>
                  <Typography weight="semi-bold" size="M" color="primary">
                    {option.name}
                  </Typography>
                  {option?.id === selectedOption?.id && <CheckIcon />}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </CSSTransition>
    </>
  );
};

export default DropdownMenu;
